import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../../shared/lib/apiClient';
import { declareTourStep, useTourStep } from '../../../shared/lib/TourContext';
import quickbooksLogo from '../../../shared/assets/logos/integrations/quickbooks.svg';
import SyncBanner from '../../../shared/components/SyncBanner';

const PROVIDER_META = {
  quickbooks: { name: 'QuickBooks', logo: quickbooksLogo },
};

// ---------------------------------------------------------------------------
// Map API bill → UI bill shape
// ---------------------------------------------------------------------------

function mapBill(b) {
  const source = b.source_integration || (b.document_store ? 'upload' : 'manual');
  return {
    id: b.id,
    vendorName: b.vendors?.display_name || b.vendor_name || 'Unknown vendor',
    vendorId: b.vendor_id,
    billNumber: b.bill_number,
    billDate: b.bill_date,
    dueDate: b.due_date,
    amount: parseFloat(b.total_amount) || 0,
    amountPaid: Math.max(0, (parseFloat(b.total_amount) || 0) - (parseFloat(b.balance_due) || 0)),
    currency: b.currency || 'USD',
    status: b.payment_status || 'unpaid',
    source,
    needsReview: !!b.needs_review,
    reviewReasons: b.review_reasons || [],
    paymentTerms: b.payment_terms,
    poNumber: b.po_number,
    poMatch: b.po_match || null,
    notes: b.notes || '',
    sourceDoc: b.document_store ? {
      id: b.document_store.id,
      fileName: b.document_store.file_name,
      processingStatus: b.document_store.processing_status,
    } : null,
    // line items loaded on demand in drawer
    lineItems: [],
    createdAt: b.created_at,
  };
}

function mapProcessingDoc(doc) {
  return {
    id: `processing-${doc.id}`,
    docId: doc.id,
    vendorName: doc.file_name,
    vendorId: null,
    billNumber: null,
    billDate: null,
    dueDate: null,
    amount: null,
    amountPaid: 0,
    currency: 'USD',
    status: doc.processing_status === 'failed' ? 'failed' : 'processing',
    source: 'upload',
    needsReview: false,
    reviewReasons: [],
    paymentTerms: null,
    notes: '',
    sourceDoc: { id: doc.id, fileName: doc.file_name, processingStatus: doc.processing_status },
    lineItems: [],
    isProcessing: true,
    errorMessage: doc.error_message,
    createdAt: doc.created_at,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateTime = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const isOverdue = (bill) => bill.status !== 'paid' && bill.dueDate && new Date(bill.dueDate) < new Date();

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + 'T00:00:00') - new Date()) / 86400000);
};

// ---------------------------------------------------------------------------
// Status badge — matches CalendarView StatusBadge style exactly
// ---------------------------------------------------------------------------

const STATUS_META = {
  unpaid:         { label: 'Unpaid',      badge: 'bg-zinc-100 text-zinc-600 border-zinc-200',           dot: 'bg-zinc-400'    },
  overdue:        { label: 'Overdue',     badge: 'bg-red-50 text-red-700 border-red-100',               dot: 'bg-red-500'     },
  partially_paid: { label: 'Partial',     badge: 'bg-amber-50 text-amber-700 border-amber-100',         dot: 'bg-amber-400'   },
  paid:           { label: 'Paid',        badge: 'bg-emerald-50 text-emerald-700 border-emerald-100',   dot: 'bg-emerald-500' },
  review:         { label: 'Review',      badge: 'bg-amber-50 text-amber-700 border-amber-200',         dot: 'bg-amber-500'   },
  processing:     { label: 'Processing',  badge: 'bg-blue-50 text-blue-700 border-blue-100',            dot: 'bg-blue-400'    },
  failed:         { label: 'Error',       badge: 'bg-red-50 text-red-700 border-red-100',               dot: 'bg-red-500'     },
};

const StatusBadge = ({ bill, confirmed }) => {
  const key = bill.needsReview && !confirmed ? 'review' : isOverdue(bill) ? 'overdue' : bill.status;
  const { label, badge, dot } = STATUS_META[key] || STATUS_META.unpaid;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border font-medium ${badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// StatCard — identical to CalendarView StatCard
// ---------------------------------------------------------------------------

const StatCard = ({ label, value, sub, highlight }) => (
  <div className={`bg-white border rounded-xl p-3 sm:p-4 ${highlight ? 'border-red-100' : 'border-zinc-200'}`}>
    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">{label}</p>
    <p className={`mt-1 sm:mt-1.5 text-lg sm:text-2xl font-light tabular-nums ${highlight ? 'text-red-600' : 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[11px] mt-0.5 sm:mt-1 text-zinc-400">{sub}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Review warning — left-border style matching existing alert patterns
// ---------------------------------------------------------------------------

const REVIEW_COPY = {
  new_vendor:       { title: 'New vendor — confirmation required', body: "Couldn't match to a known vendor. Confirm to activate this bill." },
  missing_due_date: { title: 'Due date estimated', body: 'No due date found on document — estimated as invoice date + 30 days.' },
  extraction_gap:   { title: 'Extraction incomplete', body: 'Some fields could not be read from the document. Verify before confirming.' },
};

const ReviewSection = ({ reasons, onConfirm, confirming }) => {
  if (!reasons?.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500 shrink-0">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <p className="text-xs font-semibold text-amber-900">This bill needs your attention</p>
      </div>
      <div className="space-y-2 pl-[22px]">
        {reasons.map(r => {
          const { title, body } = REVIEW_COPY[r] || { title: r, body: '' };
          return (
            <div key={r}>
              <p className="text-[11px] font-medium text-amber-800">{title}</p>
              <p className="text-[11px] text-amber-700 leading-relaxed">{body}</p>
            </div>
          );
        })}
      </div>
      <button
        onClick={onConfirm}
        disabled={confirming}
        className="w-full px-4 py-2.5 bg-amber-600 text-white text-[10px] uppercase tracking-widest font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-40"
      >
        {confirming ? 'Confirming…' : 'Confirm & Activate Bill'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bill detail drawer
// ---------------------------------------------------------------------------

const CollapsibleSection = ({ title, count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-1 group"
      >
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">{title}</p>
          {count != null && <span className="text-[10px] text-zinc-300">{count}</span>}
        </div>
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
          className={`text-zinc-300 group-hover:text-zinc-500 transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`}
        >
          <path d="M2.5 4L5 6.5L7.5 4" />
        </svg>
      </button>
      {open && <div className="mt-2.5">{children}</div>}
    </div>
  );
};

const BillDrawer = ({ bill, onClose, onConfirmed, onPaymentRecorded, onDeleted }) => {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [loadingLines, setLoadingLines] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [payingOpen, setPayingOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [payRef, setPayRef] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [slideIn, setSlideIn] = useState(false);
  const [lineSearch, setLineSearch] = useState('');
  const [repricingId, setRepricingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleRepriceLineItem = async (lineItemId) => {
    setRepricingId(lineItemId);
    try {
      const res = await apiFetch(`/api/bills/${bill.id}/line-items/${lineItemId}/reenrich`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      // Update the line item's enrichment in-place
      setLineItems(prev => prev.map(li => {
        if (li.id !== lineItemId) return li;
        const p = data.pricing || {};
        return {
          ...li,
          enrichment: {
            ...li.enrichment,
            standardized_name: li.enrichment?.standardized_name,
            normalized_unit: p.unit_normalized,
            normalized_unit_cost: p.price_per_unit,
            category: li.enrichment?.category,
          },
          unitPrice: p.price_per_unit || li.unitPrice,
          unit: p.unit_normalized || li.unit,
        };
      }));
    } catch { /* silent */ }
    finally { setRepricingId(null); }
  };

  useEffect(() => {
    requestAnimationFrame(() => setSlideIn(true));
  }, []);

  useEffect(() => {
    if (bill.isProcessing) { setLoadingLines(false); return; }
    let active = true;
    setLoadingLines(true);
    apiFetch(`/api/bills/${bill.id}/line-items`).then(r => r.json()).then(data => {
      if (active) setLineItems((data.lineItems || []).map(li => ({
        id: li.id,
        description: li.description,
        quantity: li.quantity_ordered || li.quantity_shipped || 1,
        unit: li.unit_of_measure || 'Each',
        unitPrice: parseFloat(li.unit_price) || null,
        total: parseFloat(li.extended_amount) || null,
        enrichment: li.enrichment || null,
      })));
    }).catch(() => {}).finally(() => { if (active) setLoadingLines(false); });
    return () => { active = false; };
  }, [bill.id, bill.isProcessing]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await apiFetch(`/api/bills/${bill.id}/download`);
      const data = await res.json();
      if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
    } catch {} finally { setDownloading(false); }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    await new Promise(r => setTimeout(r, 800));
    setConfirmed(true);
    setConfirming(false);
    onConfirmed?.(bill.id);
  };

  const handlePay = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { setPayError('Enter a valid amount'); return; }
    setPaying(true);
    setPayError(null);
    try {
      const res = await apiFetch(`/api/bills/${bill.id}/payment`, {
        method: 'POST',
        body: JSON.stringify({ amount: amt, payment_method: payMethod || null, reference_number: payRef || null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      setPayingOpen(false);
      setPayAmount('');
      onPaymentRecorded?.(bill.id, amt);
    } catch (err) { setPayError(err.message); }
    finally { setPaying(false); }
  };

  const handleClose = () => {
    setSlideIn(false);
    setTimeout(onClose, 200);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const docId = bill.sourceDoc?.id;
      if (docId) {
        await apiFetch(`/api/documents/${docId}`, { method: 'DELETE' });
      } else {
        // No document — delete bill directly
        await apiFetch(`/api/bills/${bill.id}`, { method: 'DELETE' });
      }
      onDeleted?.(bill.id);
      handleClose();
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const needsReview = bill.needsReview && !confirmed;
  const outstanding = bill.amount - (bill.amountPaid || 0);
  const days = daysUntil(bill.dueDate);

  const dueLabel = bill.status === 'paid'
    ? { text: 'Paid in full', cls: 'text-emerald-600' }
    : bill.amountPaid > 0
    ? { text: `${fmt(bill.amountPaid)} paid`, cls: 'text-zinc-400' }
    : days != null && days < 0
    ? { text: `${Math.abs(days)} days overdue`, cls: 'text-red-500' }
    : days === 0
    ? { text: 'Due today', cls: 'text-amber-600' }
    : days != null && days <= 7
    ? { text: `Due in ${days} days`, cls: 'text-amber-600' }
    : { text: bill.dueDate ? `Due ${fmtDate(bill.dueDate)}` : 'No due date', cls: 'text-zinc-400' };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-colors duration-200 ${slideIn ? 'bg-black/10' : 'bg-transparent'}`}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[520px] bg-white border-l border-zinc-200 shadow-2xl z-50 flex flex-col transition-transform duration-200 ease-out ${slideIn ? 'translate-x-0' : 'translate-x-full'}`}
      >

        {/* Hero Header */}
        <div className="px-4 sm:px-7 pt-5 sm:pt-6 pb-4 sm:pb-5 border-b border-zinc-100 bg-white">
          {/* Top row — close + source tag */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              {bill.sourceDoc ? (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-600 transition-colors disabled:opacity-40"
                >
                  <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" className="shrink-0">
                    <path d="M2.5 1h6l3 3v9.5H2.5V1z" /><path d="M8.5 1v3H11.5" />
                  </svg>
                  {downloading ? 'Opening...' : 'View PDF'}
                </button>
              ) : bill.source === 'quickbooks' ? (
                <span className="inline-flex items-center gap-1.5 text-[10px] text-zinc-400">
                  <img src={quickbooksLogo} alt="QB" className="w-3.5 h-3.5" />
                  QuickBooks
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Delete bill"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" />
                </svg>
              </button>
              <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 2l10 10M12 2L2 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Amount hero */}
          <p className={`text-4xl font-extralight tabular-nums tracking-tight ${isOverdue(bill) && bill.status !== 'paid' ? 'text-red-600' : 'text-zinc-900'}`}>
            {fmt(outstanding)}
          </p>

          {/* Vendor + meta row */}
          <div className="flex items-center gap-2 mt-2">
            <p className="text-sm font-medium text-zinc-700">{bill.vendorName}</p>
            {bill.billNumber && (
              <>
                <span className="text-zinc-200">·</span>
                <p className="text-xs text-zinc-400 font-mono">#{bill.billNumber}</p>
              </>
            )}
          </div>

          {/* Status + due row */}
          <div className="flex items-center gap-3 mt-3">
            <StatusBadge bill={bill} confirmed={confirmed} />
            <span className={`text-[11px] ${dueLabel.cls}`}>{dueLabel.text}</span>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-7 py-4 sm:py-5 space-y-4 sm:space-y-5">

          {/* Processing status */}
          {bill.isProcessing && (
            <div className={`flex items-center gap-3 border rounded-xl px-4 py-4 ${
              bill.status === 'failed'
                ? 'bg-red-50 border-red-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              {bill.status === 'failed' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-red-500 shrink-0">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h0" />
                </svg>
              ) : (
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin shrink-0" />
              )}
              <div>
                <p className={`text-xs font-semibold ${bill.status === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                  {bill.status === 'failed' ? 'Processing failed' : 'Processing document...'}
                </p>
                <p className={`text-[11px] mt-0.5 ${bill.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                  {bill.errorMessage || (bill.status === 'failed' ? 'Something went wrong. Try uploading again.' : 'Extracting data from your document. This usually takes a few seconds.')}
                </p>
              </div>
            </div>
          )}

          {/* Review alert — top priority */}
          {needsReview && (
            <ReviewSection reasons={bill.reviewReasons} onConfirm={handleConfirm} confirming={confirming} />
          )}
          {confirmed && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500 shrink-0">
                <path d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <p className="text-xs font-semibold text-emerald-800">Bill confirmed</p>
                <p className="text-[11px] text-emerald-700">Vendor added and bill is now active.</p>
              </div>
            </div>
          )}

          {/* Details — always visible, compact */}
          <div className="grid grid-cols-3 gap-px bg-zinc-100 rounded-xl overflow-hidden border border-zinc-100">
            {[
              ['Bill Date', fmtDate(bill.billDate)],
              ['Due Date', fmtDate(bill.dueDate)],
              ['Terms', bill.paymentTerms || '—'],
            ].map(([label, value]) => (
              <div key={label} className="bg-white px-4 py-3">
                <p className="text-[10px] text-zinc-400 mb-1">{label}</p>
                <p className="text-xs font-medium text-zinc-800">{value}</p>
              </div>
            ))}
          </div>

          {/* Line items — collapsible */}
          <CollapsibleSection
            title="Line Items"
            count={!loadingLines ? lineItems.length : null}
            defaultOpen={lineItems.length > 0 || loadingLines}
          >
            {loadingLines ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-4 h-4 border-2 border-zinc-200 border-t-zinc-500 rounded-full animate-spin" />
              </div>
            ) : lineItems.length > 0 ? (() => {
              const q = lineSearch.toLowerCase();
              const hasEnrichment = lineItems.some(li => li.enrichment);
              const filtered = q ? lineItems.filter(item => {
                const desc = (item.description || '').toLowerCase();
                const eName = (item.enrichment?.standardized_name || '').toLowerCase();
                const eCat = (item.enrichment?.category || '').toLowerCase();
                return desc.includes(q) || eName.includes(q) || eCat.includes(q);
              }) : lineItems;
              return (
                <div className="border border-zinc-200 rounded-xl overflow-hidden">
                  {lineItems.length > 5 && (
                    <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50/50">
                      <div className="relative">
                        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <input
                          type="text"
                          value={lineSearch}
                          onChange={e => setLineSearch(e.target.value)}
                          placeholder="Search line items..."
                          className="w-full pl-7 pr-3 py-1.5 text-[11px] bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-400 text-zinc-700 placeholder:text-zinc-300"
                        />
                      </div>
                    </div>
                  )}
                  <div className="max-h-[320px] overflow-y-auto">
                    {filtered.length > 0 ? filtered.map((item, i) => {
                      const e = item.enrichment;
                      return (
                        <div key={i} className={`flex items-start justify-between px-4 py-2.5 ${i < filtered.length - 1 ? 'border-b border-zinc-50' : ''}`}>
                          <div className="flex-1 min-w-0 pr-4">
                            <p className="text-xs text-zinc-800 leading-snug">{e?.standardized_name || item.description}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">
                              {item.quantity} {e?.normalized_unit || item.unit} @ {fmt(e?.normalized_unit_cost ?? item.unitPrice)}
                              {e?.comparison_unit && e.comparison_unit !== e.normalized_unit && e.comparison_unit_cost != null && (
                                <span className="text-zinc-300"> · {fmt(e.comparison_unit_cost)}/{e.comparison_unit}</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {e?.price_vs_history && (
                              <span className={`text-[9px] font-medium ${/^up /i.test(e.price_vs_history) ? 'text-red-500' : /^down /i.test(e.price_vs_history) ? 'text-emerald-600' : 'text-zinc-400'}`}>
                                {e.price_vs_history.replace(/\s+on\s+\d{4}-\d{2}-\d{2}$/, '')}
                              </span>
                            )}
                            <span className="text-xs font-medium text-zinc-800 tabular-nums">{fmt(item.total)}</span>
                            {item.id && (
                              <button
                                onClick={() => handleRepriceLineItem(item.id)}
                                disabled={repricingId === item.id}
                                title="Re-price this item"
                                className="p-1 rounded hover:bg-zinc-100 text-zinc-300 hover:text-zinc-500 transition-colors disabled:opacity-50"
                              >
                                {repricingId === item.id ? (
                                  <div className="w-3 h-3 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2.5 8A5.5 5.5 0 1 0 4 4.1" />
                                    <path d="M2 2v4h4" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <p className="text-[11px] text-zinc-300 py-4 text-center">No matches</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 border-t border-zinc-100">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">{q ? `${filtered.length} of ${lineItems.length}` : 'Total'}</span>
                    <span className="text-sm font-medium text-zinc-900 tabular-nums">{fmt(q ? filtered.reduce((s, li) => s + (li.total || 0), 0) : bill.amount)}</span>
                  </div>
                </div>
              );
            })() : (
              <p className="text-[11px] text-zinc-300 py-2">No line items extracted</p>
            )}
          </CollapsibleSection>

          {/* Notes — collapsible */}
          <CollapsibleSection title="Notes" defaultOpen={!!bill.notes}>
            {bill.notes ? (
              <p className="text-xs text-zinc-700 leading-relaxed bg-zinc-50 rounded-xl px-4 py-3 border border-zinc-100">{bill.notes}</p>
            ) : (
              <button className="w-full text-left text-[11px] text-zinc-400 hover:text-zinc-600 transition-colors py-1">
                + Add a note
              </button>
            )}
          </CollapsibleSection>

        </div>

        {/* Footer — payment */}
        {bill.status !== 'paid' && !needsReview && (
          <div className="px-4 sm:px-7 py-4 border-t border-zinc-100 bg-white">
            {!payingOpen ? (
              <button
                onClick={() => { setPayAmount(String(outstanding.toFixed(2))); setPayingOpen(true); }}
                className="w-full px-4 py-2.5 border border-zinc-900 bg-zinc-900 text-white text-[10px] uppercase tracking-widest font-medium rounded-lg hover:bg-zinc-700 hover:border-zinc-700 transition-colors"
              >
                Record Payment
              </button>
            ) : (
              <div className="space-y-2.5">
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    placeholder="Amount"
                    className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-lg outline-none focus:border-zinc-400"
                  />
                  <select
                    value={payMethod}
                    onChange={e => setPayMethod(e.target.value)}
                    className="px-3 py-2 text-xs border border-zinc-200 rounded-lg outline-none focus:border-zinc-400 bg-white"
                  >
                    <option value="">Method</option>
                    <option value="check">Check</option>
                    <option value="ach">ACH</option>
                    <option value="wire">Wire</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <input
                  type="text"
                  value={payRef}
                  onChange={e => setPayRef(e.target.value)}
                  placeholder="Reference # (optional)"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg outline-none focus:border-zinc-400"
                />
                {payError && <p className="text-[11px] text-red-500">{payError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handlePay}
                    disabled={paying}
                    className="flex-1 px-4 py-2.5 border border-zinc-900 bg-zinc-900 text-white text-[10px] uppercase tracking-widest font-medium rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-40"
                  >
                    {paying ? 'Saving...' : 'Save Payment'}
                  </button>
                  <button
                    onClick={() => { setPayingOpen(false); setPayError(null); }}
                    className="px-4 py-2.5 border border-zinc-200 text-zinc-500 text-[10px] uppercase tracking-widest font-medium rounded-lg hover:border-zinc-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xs p-5">
            <h3 className="text-sm font-semibold text-zinc-900">Delete this bill?</h3>
            <p className="text-xs text-zinc-500 mt-2">
              This will permanently delete the bill, all line items, payables entries, and the source document.
            </p>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

const STATUS_TEXT = {
  unpaid:         { label: 'Unpaid',      cls: 'text-zinc-500'    },
  overdue:        { label: 'Overdue',     cls: 'text-red-600'     },
  partially_paid: { label: 'Partial',     cls: 'text-amber-600'   },
  paid:           { label: 'Paid',        cls: 'text-emerald-600' },
  review:         { label: 'Review',      cls: 'text-amber-600'   },
  processing:     { label: 'Processing',  cls: 'text-blue-500'    },
  failed:         { label: 'Error',       cls: 'text-red-500'     },
};

const BillRow = ({ bill, onClick, index, loaded }) => {
  const isProc = bill.isProcessing;
  const overdue = isOverdue(bill);
  const outstanding = isProc ? null : bill.amount - (bill.amountPaid || 0);
  const statusKey = isProc ? bill.status : bill.needsReview ? 'review' : overdue ? 'overdue' : bill.status;
  const { label: statusLabel, cls: statusCls } = STATUS_TEXT[statusKey] || STATUS_TEXT.unpaid;

  const days = daysUntil(bill.dueDate);
  const dueCls = bill.status === 'paid'
    ? 'text-zinc-400'
    : days != null && days < 0
    ? 'text-red-600'
    : days === 0 || (days != null && days <= 7)
    ? 'text-amber-600'
    : 'text-zinc-500';

  return (
    <tr
      onClick={() => onClick(bill)}
      className={`group border-b border-zinc-100 last:border-0 bg-white transition-colors hover:bg-zinc-50/50 cursor-pointer ${isProc ? 'opacity-75' : ''}`}
      style={{ opacity: loaded ? (isProc ? 0.75 : 1) : 0, transition: `opacity 0.4s cubic-bezier(0.16,1,0.3,1) ${400 + index * 30}ms` }}
    >
      {/* Vendor — sticky */}
      <td className="sticky left-0 z-10 bg-white pl-3 pr-2 py-1.5 border-r border-zinc-200 group-hover:bg-zinc-50/50" style={{ minWidth: 200 }}>
        <div className="flex items-center gap-2">
          {isProc && statusKey === 'processing' && (
            <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin shrink-0" />
          )}
          <span className={`text-[12px] font-medium whitespace-nowrap ${isProc ? 'text-zinc-500 truncate max-w-[180px]' : 'text-zinc-900'}`}>
            {bill.vendorName}
          </span>
          {bill.needsReview && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-600 border border-amber-200 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0">
              <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0" />
              Review
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-400 mt-0.5">
          {isProc ? (bill.errorMessage || 'Reading document...') : bill.source === 'quickbooks' ? 'QuickBooks' : 'Uploaded'}
        </p>
      </td>

      {/* Bill # */}
      <td className="bg-stone-50/80 px-3 py-1.5">
        <span className="text-[12px] font-mono text-zinc-500 whitespace-nowrap">{bill.billNumber ? `#${bill.billNumber}` : '—'}</span>
      </td>

      {/* Bill Date */}
      <td className="bg-stone-50/80 px-3 py-1.5">
        <span className="text-[12px] text-zinc-500 whitespace-nowrap">{fmtDate(bill.billDate)}</span>
      </td>

      {/* Due Date */}
      <td className="bg-stone-50/80 px-3 py-1.5">
        <span className={`text-[12px] whitespace-nowrap ${dueCls}`}>{fmtDate(bill.dueDate)}</span>
      </td>

      {/* Amount */}
      <td className="bg-stone-50/80 px-3 py-1.5 text-right">
        <span className={`text-[12px] font-light tabular-nums whitespace-nowrap ${
          isProc ? 'text-zinc-300' : bill.status === 'paid' ? 'text-zinc-400' : overdue ? 'text-red-600' : 'text-zinc-900'
        }`}>
          {isProc ? '—' : fmt(outstanding)}
        </span>
      </td>

      {/* PO Match */}
      <td className="bg-stone-50/80 px-3 py-1.5">
        {bill.poMatch ? (
          <span className={`text-[11px] font-medium ${
            bill.poMatch.match_type === 'full_match' ? 'text-emerald-600' :
            bill.poMatch.match_type === 'match_with_discrepancies' ? 'text-amber-600' :
            bill.poMatch.match_type === 'partial_match' ? 'text-blue-600' :
            'text-zinc-400'
          }`}>
            {bill.poMatch.match_type === 'full_match' ? '✓ Matched' :
             bill.poMatch.match_type === 'match_with_discrepancies' ? `⚠ ${bill.poMatch.discrepancies?.length || 0} issue${(bill.poMatch.discrepancies?.length || 0) !== 1 ? 's' : ''}` :
             bill.poMatch.match_type === 'partial_match' ? `${bill.poMatch.matched_lines}/${bill.poMatch.total_lines}` :
             '—'}
          </span>
        ) : bill.poNumber ? (
          <span className="text-[11px] text-zinc-400">{bill.poNumber}</span>
        ) : (
          <span className="text-[11px] text-zinc-300">—</span>
        )}
      </td>

      {/* Status */}
      <td className="bg-stone-50/80 px-3 py-1.5">
        <span className={`text-[12px] font-medium ${statusCls}`}>{statusLabel}</span>
      </td>

      {/* Chevron */}
      <td className="bg-stone-50/80 pr-3 py-1.5 text-right">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="inline text-zinc-300 group-hover:text-zinc-400 transition-colors">
          <path d="M4.5 2.5l3 3-3 3" />
        </svg>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Mobile bill card
// ---------------------------------------------------------------------------

const BillCard = ({ bill, onClick, index, loaded }) => {
  const isProc = bill.isProcessing;
  const overdue = isOverdue(bill);
  const outstanding = isProc ? null : bill.amount - (bill.amountPaid || 0);
  const statusKey = isProc ? bill.status : bill.needsReview ? 'review' : overdue ? 'overdue' : bill.status;
  const { label: statusLabel, cls: statusCls } = STATUS_TEXT[statusKey] || STATUS_TEXT.unpaid;

  return (
    <button
      onClick={() => onClick(bill)}
      className={`w-full text-left px-3 py-3 border-b border-zinc-100 last:border-0 bg-white transition-colors active:bg-zinc-50 ${isProc ? 'opacity-75' : ''}`}
      style={{ opacity: loaded ? (isProc ? 0.75 : 1) : 0, transition: `opacity 0.4s cubic-bezier(0.16,1,0.3,1) ${400 + index * 30}ms` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isProc && statusKey === 'processing' && (
              <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin shrink-0" />
            )}
            <span className={`text-[13px] font-medium ${isProc ? 'text-zinc-500 truncate' : 'text-zinc-900'}`}>
              {bill.vendorName}
            </span>
            {bill.needsReview && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-600 border border-amber-200 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0">
                <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0" />
                Review
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-zinc-400">
              {isProc ? (bill.errorMessage || 'Reading document...') : bill.source === 'quickbooks' ? 'QuickBooks' : 'Uploaded'}
            </span>
            {bill.billNumber && (
              <>
                <span className="text-zinc-200">·</span>
                <span className="text-[11px] font-mono text-zinc-400">#{bill.billNumber}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className={`text-[13px] font-medium tabular-nums ${
            isProc ? 'text-zinc-300' : bill.status === 'paid' ? 'text-zinc-400' : overdue ? 'text-red-600' : 'text-zinc-900'
          }`}>
            {isProc ? '—' : fmt(outstanding)}
          </span>
          <div className="flex items-center gap-1.5 justify-end mt-0.5">
            <span className={`text-[11px] font-medium ${statusCls}`}>{statusLabel}</span>
            {bill.dueDate && !isProc && (
              <>
                <span className="text-zinc-200">·</span>
                <span className="text-[11px] text-zinc-400">{fmtDate(bill.dueDate)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

declareTourStep({
  id: 'bills-page',
  title: 'Bills Workspace',
  description: 'Your invoice pipeline. Review documents, monitor processing, and work through what needs attention.',
  priority: 40,
  path: '/dashboard/bills',
});

const BillsView = ({ loaded }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('filter') || null);
  const [showFilterDd, setShowFilterDd] = useState(false);

  const billsPageRef = useTourStep('bills-page');
  const [sortCol, setSortCol] = useState('dueDate');
  const [sortDir, setSortDir] = useState('desc');
  const [syncStatus, setSyncStatus] = useState(null); // null | { provider, status: 'syncing'|'done'|'error', name, logo }
  const [activeIntegrations, setActiveIntegrations] = useState([]);
  const filterDdRef = useRef(null);

  // Clear URL filter param after applying (so back navigation is clean)
  useEffect(() => {
    if (searchParams.has('filter')) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only integrations that have a sync adapter (bills sync)
  const SYNCABLE_PROVIDERS = new Set(['quickbooks']);

  const STATUS_FILTER_OPTIONS = [
    { id: 'needs_review',  label: 'Needs Review'  },
    { id: 'unpaid',        label: 'Unpaid'        },
    { id: 'overdue',       label: 'Overdue'       },
    { id: 'due_today',     label: 'Due Today'     },
    { id: 'arrived_today', label: 'Arrived Today' },
    { id: 'pending_sync',  label: 'Pending Sync'  },
    { id: 'paid',          label: 'Paid'          },
  ];

  const fetchBills = useCallback(async () => {
    try {
      setFetchError(null);

      const res = await apiFetch('/api/bills');
      if (!res.ok) throw new Error('Failed to load bills');
      const data = await res.json();
      const mapped = (data.bills || []).map(mapBill);
      const processing = (data.processing || []).map(mapProcessingDoc);
      setBills([...processing, ...mapped]);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  // Sync a single provider (used by auto-sync and manual button)
  const syncProvider = useCallback(async (provider) => {
    const meta = PROVIDER_META[provider] || { name: provider, logo: null };
    setSyncStatus({ provider, status: 'syncing', name: meta.name, logo: meta.logo });
    try {
      const sr = await apiFetch(`/api/sync/${provider}`, { method: 'POST' });
      const d = sr.ok ? await sr.json() : null;
      setSyncStatus({ provider, status: 'done', name: meta.name, logo: meta.logo });
      if (d?.bills?.synced > 0 || d?.payments?.synced > 0) fetchBills();
      setTimeout(() => setSyncStatus(null), 3000);
    } catch {
      setSyncStatus({ provider, status: 'error', name: meta.name, logo: meta.logo });
      setTimeout(() => setSyncStatus(null), 4000);
    }
  }, [fetchBills]);

  // Auto-sync on page load if preference is enabled (throttled per session)
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const [intRes, prefRes] = await Promise.all([
          apiFetch('/api/integrations').then(r => r.json()).catch(() => ({ integrations: [] })),
          apiFetch('/api/sync/preferences').then(r => r.json()).catch(() => ({ preferences: [] })),
        ]);
        const syncable = (intRes.integrations || []).filter(i => i.status === 'active' && SYNCABLE_PROVIDERS.has(i.provider));
        if (!cancelled) setActiveIntegrations(syncable);

        // Only auto-sync if bills preference is enabled
        const billsPref = (prefRes.preferences || []).find(p => p.entity === 'bills' && p.enabled);
        if (!billsPref) return;

        for (const int of syncable) {
          if (cancelled) break;
          const key = `arbcor_sync_bills_${int.provider}`;
          const last = parseInt(sessionStorage.getItem(key) || '0', 10);
          if (Date.now() - last < 5 * 60 * 1000) continue;
          sessionStorage.setItem(key, String(Date.now()));
          await syncProvider(int.provider);
        }
      } catch { /* skip */ }
    }
    init();
    return () => { cancelled = true; };
  }, [syncProvider]);

  useEffect(() => {
    const handler = (e) => {
      if (filterDdRef.current && !filterDdRef.current.contains(e.target)) setShowFilterDd(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleConfirmed = (billId) => {
    setBills(prev => prev.map(b =>
      b.id === billId ? { ...b, needsReview: false, reviewReasons: [] } : b
    ));
  };

  const realBills = useMemo(() => bills.filter(b => !b.isProcessing), [bills]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'vendorName' ? 'asc' : 'desc'); }
  };

  const filtered = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const list = bills.filter(b => {
      if (b.isProcessing) return !statusFilter;

      const matchesStatus =
        !statusFilter ? true :
        statusFilter === 'needs_review' ? b.needsReview :
        statusFilter === 'unpaid' ? b.status === 'unpaid' && !isOverdue(b) :
        statusFilter === 'overdue' ? isOverdue(b) :
        statusFilter === 'due_today' ? b.dueDate === todayStr && b.status === 'unpaid' :
        statusFilter === 'arrived_today' ? (b.createdAt && b.createdAt.slice(0, 10) === todayStr) :
        statusFilter === 'pending_sync' ? b.source !== 'quickbooks' && b.status === 'unpaid' :
        statusFilter === 'paid' ? b.status === 'paid' : true;

      const q = search.toLowerCase().trim();
      const matchesSearch = !q ||
        b.vendorName.toLowerCase().includes(q) ||
        (b.billNumber || '').toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      // Keep processing rows at the top
      if (a.isProcessing !== b.isProcessing) return a.isProcessing ? -1 : 1;
      if (a.isProcessing) return 0;

      let av, bv;
      switch (sortCol) {
        case 'vendorName':
          av = (a.vendorName || '').toLowerCase();
          bv = (b.vendorName || '').toLowerCase();
          return av < bv ? -dir : av > bv ? dir : 0;
        case 'billNumber':
          av = (a.billNumber || '').toLowerCase();
          bv = (b.billNumber || '').toLowerCase();
          return av < bv ? -dir : av > bv ? dir : 0;
        case 'billDate':
          av = a.billDate || '';
          bv = b.billDate || '';
          return av < bv ? -dir : av > bv ? dir : 0;
        case 'dueDate':
          av = a.dueDate || '';
          bv = b.dueDate || '';
          return av < bv ? -dir : av > bv ? dir : 0;
        case 'amount':
          av = (a.amount || 0) - (a.amountPaid || 0);
          bv = (b.amount || 0) - (b.amountPaid || 0);
          return (av - bv) * dir;
        case 'status':
          av = a.needsReview ? 'review' : isOverdue(a) ? 'overdue' : a.status;
          bv = b.needsReview ? 'review' : isOverdue(b) ? 'overdue' : b.status;
          return av < bv ? -dir : av > bv ? dir : 0;
        default:
          return 0;
      }
    });

    return list;
  }, [bills, statusFilter, search, sortCol, sortDir]);

  const totalOutstanding = realBills
    .filter(b => b.status !== 'paid')
    .reduce((s, b) => s + (b.amount - (b.amountPaid || 0)), 0);
  const totalOverdue = realBills
    .filter(isOverdue)
    .reduce((s, b) => s + (b.amount - (b.amountPaid || 0)), 0);
  const reviewCount = realBills.filter(b => b.needsReview).length;
  const overdueCount = realBills.filter(isOverdue).length;

  return (
    <div className="flex flex-col max-w-[1400px] mx-auto space-y-3 sm:space-y-5" style={{ height: 'calc(100vh - 104px)' }}>

      {/* Header */}
      <div
        ref={billsPageRef}
        className="flex items-start justify-between gap-3 shrink-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '200ms' }}
      >
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-zinc-900">Bills</h1>
          <p className="text-xs sm:text-sm text-zinc-400 mt-0.5">Vendor invoices you need to pay</p>
        </div>
        <button className="px-3 sm:px-4 py-2 border border-zinc-200 text-zinc-600 text-[10px] uppercase tracking-widest font-medium rounded-xl hover:border-zinc-900 hover:text-zinc-900 transition-colors shrink-0">
          + Add Bill
        </button>
      </div>

      {/* Integration sync bar — disabled, replaced by SyncBanner */}

      {/* Sync banner — contextual prompt */}
      {!loading && (
        <SyncBanner
          entity="bills"
          label="bills"
          onSynced={fetchBills}
        />
      )}

      {/* Stat cards */}
      <div
        className="grid grid-cols-3 gap-2 sm:gap-4 shrink-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '270ms' }}
      >
        <StatCard
          label="Outstanding"
          value={`$${Number(totalOutstanding).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`${bills.filter(b => b.status !== 'paid').length} open bills`}
        />
        <StatCard
          label="Overdue"
          value={`$${Number(totalOverdue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`${overdueCount} past due`}
          highlight={totalOverdue > 0}
        />
        <StatCard
          label="Needs Review"
          value={String(reviewCount)}
          sub="pending confirmation"
          highlight={reviewCount > 0}
        />
      </div>

      {/* Filter bar — matches VendorEcosystemView */}
      <div
        className="flex flex-wrap items-center gap-2 sm:gap-2.5 shrink-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-20"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: '340ms' }}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-0 sm:max-w-[240px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter Bills By Vendor..."
            className="w-full pl-8 pr-3 py-1.5 text-[12px] text-stone-900 placeholder-stone-400 border border-stone-200 rounded-lg outline-none focus:border-stone-400 transition-colors"
          />
        </div>

        {/* Active filter pill — centered */}
        <div className="hidden sm:flex items-center gap-1.5 flex-1 justify-center">
          {statusFilter && (
            <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-medium bg-stone-100 text-stone-700 border border-stone-200">
              Status
              <span className="text-stone-400 mx-0.5">·</span>
              {STATUS_FILTER_OPTIONS.find(o => o.id === statusFilter)?.label}
              <button onClick={() => setStatusFilter(null)} className="ml-0.5 rounded-full hover:bg-stone-200 p-0.5 transition-colors">
                <svg width="8" height="8" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="2"><path d="M2.5 2.5l5 5M7.5 2.5l-5 5" /></svg>
              </button>
            </span>
          )}
        </div>

        {/* Filter button */}
        <div className="relative shrink-0" ref={filterDdRef}>
          <button
            onClick={() => setShowFilterDd(o => !o)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-colors ${
              statusFilter ? 'border-stone-400 text-stone-900 bg-stone-50' : 'border-stone-200 text-stone-500 hover:border-stone-300'
            }`}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filter
          </button>
          {showFilterDd && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-30 py-1 min-w-[160px]">
              <div className="px-3 py-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Status</div>
              {STATUS_FILTER_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { setStatusFilter(opt.id); setShowFilterDd(false); }}
                  className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-stone-50 flex items-center gap-2 ${
                    statusFilter === opt.id ? 'text-stone-900 font-medium' : 'text-stone-600'
                  }`}
                >
                  {statusFilter === opt.id && <span className="w-1.5 h-1.5 rounded-full bg-stone-900 shrink-0" />}
                  {opt.label}
                </button>
              ))}
              {statusFilter && (
                <>
                  <div className="border-t border-stone-100 my-1" />
                  <button
                    onClick={() => { setStatusFilter(null); setShowFilterDd(false); }}
                    className="w-full text-left px-3 py-1.5 text-[11px] text-stone-400 hover:bg-stone-50"
                  >
                    Clear filter
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div
        className="border border-zinc-200 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-10 flex flex-col min-h-0 flex-1"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '410ms' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 bg-white">
            <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white text-zinc-400 gap-2">
            <p className="text-[13px] text-red-500">{fetchError}</p>
            <button onClick={fetchBills} className="text-[11px] text-zinc-500 hover:text-zinc-700 underline">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white text-zinc-400">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" className="mb-3 opacity-30">
              <rect x="3" y="3" width="22" height="22" rx="2.5" /><path d="M9 10h10M9 14.5h7M9 19h5" />
            </svg>
            <p className="text-[13px]">{realBills.length === 0 ? 'No bills yet' : 'No bills match your filter'}</p>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="sm:hidden flex-1 min-h-0 overflow-y-auto">
              {filtered.map((bill, i) => (
                <BillCard key={bill.id} bill={bill} onClick={setSelectedBill} index={i} loaded={loaded} />
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto flex-1 min-h-0 overflow-y-auto">
              <table className="w-full border-collapse" style={{ minWidth: 780 }}>
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-zinc-200 bg-white">
                    {[
                      { key: 'vendorName', label: 'Vendor', sticky: true, minW: 200 },
                      { key: 'billNumber', label: 'Bill #', minW: 120 },
                      { key: 'billDate', label: 'Bill Date', minW: 110 },
                      { key: 'dueDate', label: 'Due Date', minW: 110 },
                      { key: 'amount', label: 'Amount', minW: 110, align: 'right' },
                      { key: 'poMatch', label: 'PO Match', minW: 110 },
                      { key: 'status', label: 'Status', minW: 100 },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => toggleSort(col.key)}
                        className={`${col.sticky ? 'sticky left-0 z-30 bg-white border-r border-zinc-200 pl-3 pr-2' : 'bg-stone-50/80 px-3'} py-2 text-[11px] text-zinc-500 font-medium text-${col.align || 'left'} cursor-pointer select-none hover:text-zinc-800 transition-colors group/th`}
                        style={{ minWidth: col.minW }}
                      >
                        <span className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'justify-end w-full' : ''}`}>
                          {col.label}
                          <svg
                            width="8" height="8" viewBox="0 0 8 8" fill="none"
                            className={`shrink-0 transition-all ${sortCol === col.key ? 'opacity-100 text-zinc-700' : 'opacity-0 group-hover/th:opacity-40'}`}
                          >
                            <path
                              d={sortCol === col.key && sortDir === 'asc' ? 'M1.5 5L4 2.5L6.5 5' : 'M1.5 3L4 5.5L6.5 3'}
                              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      </th>
                    ))}
                    <th className="bg-stone-50/80 pr-3 py-2" style={{ width: 32 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((bill, i) => (
                    <BillRow key={bill.id} bill={bill} onClick={setSelectedBill} index={i} loaded={loaded} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50/40 shrink-0">
              <span className="text-[10px] text-zinc-400">
                {filtered.length} bill{filtered.length !== 1 ? 's' : ''}
                {filtered.length !== bills.length && ` of ${bills.length}`}
              </span>
            </div>
          </>
        )}
      </div>

      {selectedBill && (
        <BillDrawer
          bill={selectedBill}
          onClose={() => setSelectedBill(null)}
          onConfirmed={handleConfirmed}
          onPaymentRecorded={(billId, amt) => {
            setBills(prev => prev.map(b =>
              b.id === billId ? { ...b, amountPaid: b.amountPaid + amt, status: (b.amountPaid + amt) >= b.amount ? 'paid' : 'partially_paid' } : b
            ));
            setSelectedBill(prev => prev ? { ...prev, amountPaid: prev.amountPaid + amt, status: (prev.amountPaid + amt) >= prev.amount ? 'paid' : 'partially_paid' } : null);
          }}
          onDeleted={(billId) => {
            setBills(prev => prev.filter(b => b.id !== billId));
            setSelectedBill(null);
          }}
        />
      )}
    </div>
  );
};

export default BillsView;
